import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Store } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import loginImg from "@/assets/loginimage.jpg";

const PORTALS = [
  {
    key: "salesperson",
    label: "Sales Executive",
    desc: "Visualize products for a walk-in customer",
    icon: Sparkles,
  },
  {
    key: "retailer",
    label: "Retailer",
    desc: "Manage your catalog, team & credits",
    icon: Store,
    heading: "Retailer Login",
    sub: "Sign in to your retailer dashboard.",
  },
];

export default function Login() {
  const nav = useNavigate();
  const { login, setUser, user, loading } = useAuth();
  const [portal, setPortal] = useState(null);

  React.useEffect(() => {
    if (!loading && user) {
      const roleHome = user.role === "salesperson" ? "/sales" : user.role === "admin" ? "/admin" : "/dashboard";
      nav(roleHome, { replace: true });
    }
  }, [user, loading, nav]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      if (user.role === "admin") {
        toast.error("Please use the admin login page.");
        return;
      }
      if (portal === "salesperson" && user.role !== "salesperson") {
        toast.error("This account is not a Sales Executive account.");
        return;
      }
      if (portal === "retailer" && user.role !== "retailer") {
        toast.error("This account is not a Retailer account.");
        return;
      }
      toast.success(`Welcome back, ${user.business_name || user.email}`);
      const roleHome = user.role === "salesperson" ? "/sales" : "/dashboard";
      nav(roleHome, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      if (err?.response?.status === 403 && detail.toLowerCase().includes("verify")) {
        toast.error(
          <span>
            Email not verified.{" "}
            <button
              className="underline font-medium"
              onClick={() => nav("/verify-email-sent", { state: { email } })}
            >
              Resend link
            </button>
          </span>
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse) => {
    setBusy(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      const response = await fetch(`${backendUrl}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Google login failed");
      
      localStorage.setItem("neivai_token", data.token);
      localStorage.setItem("neivai_user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success(`Welcome, ${data.user.business_name || data.user.email}`);
      const roleHome = data.user.role === "salesperson" ? "/sales" : "/dashboard";
      nav(roleHome, { replace: true });
    } catch (err) {
      toast.error(err.message || "Google login failed");
      setBusy(false);
    }
  };

  const onGoogleError = () => {
    toast.error("Google login failed");
  };

  const active = PORTALS.find((p) => p.key === portal);

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:block relative">
        <img src={loginImg} alt="Interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1C1E]/50 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <span className="overline text-white/80">The design studio in your pocket</span>
          <h2 className="mt-4 text-4xl font-display font-medium leading-tight">See it before you sell it.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-16">
        <div className="w-full max-w-md">
          <Logo size="md" />

          {!active ? (
            <>
              <h1 className="mt-12 text-4xl font-display font-semibold tracking-tight">Welcome back</h1>
              <p className="mt-2 text-[#5C5F62]">Choose how you'd like to sign in.</p>
              <div className="mt-10 space-y-3">
                {PORTALS.map((p) => (
                  <button
                    key={p.key}
                    data-testid={`portal-${p.key}`}
                    onClick={() => p.key === "salesperson" ? nav("/sales/login") : setPortal(p.key)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#E5E5E5] bg-white text-left hover:border-[#C86B53] transition"
                  >
                    <span className="h-10 w-10 rounded-full bg-[#F3F1EC] flex items-center justify-center shrink-0">
                      <p.icon className="h-5 w-5 text-[#C86B53]" />
                    </span>
                    <span>
                      <span className="block font-medium">{p.label}</span>
                      <span className="block text-xs text-[#8A8D91]">{p.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-8 text-sm text-[#5C5F62]">
                New to NEIV? <Link to="/signup" className="text-[#C86B53] font-medium hover:underline">Create an account</Link>
              </p>
            </>
          ) : (
            <>
              <button
                data-testid="portal-back-btn"
                onClick={() => setPortal(null)}
                className="mt-12 flex items-center gap-1 text-sm text-[#5C5F62] hover:text-[#1A1C1E] transition"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <h1 className="mt-4 text-4xl font-display font-semibold tracking-tight">{active.heading}</h1>
              <p className="mt-2 text-[#5C5F62]">{active.sub}</p>
              
              <form className="mt-10 space-y-5" onSubmit={onSubmit}>
                <div>
                  <label className="text-sm font-medium text-[#1A1C1E]">Email</label>
                  <input
                    data-testid="login-email-input"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                    placeholder="you@studio.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1C1E]">Password</label>
                  <input
                    data-testid="login-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                    placeholder="••••••••"
                  />
                </div>
                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-xs text-[#5C5F62] hover:text-[#C86B53] transition">Forgot password?</Link>
                </div>
                <button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={busy}
                  className="neivai-btn-primary w-full disabled:opacity-60"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="mt-8 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E5E5E5]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-[#5C5F62]">Or continue with</span>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <GoogleLogin
                  onSuccess={onGoogleSuccess}
                  onError={onGoogleError}
                  text="signin_with"
                  theme="outline"
                  size="large"
                />
              </div>

              <p className="mt-6 text-center text-sm text-[#5C5F62]">
                Don't have an account? <Link to="/signup" className="text-[#C86B53] font-medium hover:underline">Sign up</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
